import { Platform } from 'react-native';
import { supabase, toError } from './supabase';
import {
    buildProjectLimitMessageForSnapshot,
    canCreateProjectForSnapshot,
    requiresSubscriptionForProjectCreation,
    shouldBlockProjectCreationForSnapshot,
} from './projectCreationEntitlements';
import { shouldPromptBillingPreviewCheckout } from './uiEntitlementFeatures';
import { getLegacyUpgradeAction, isLegacyAccessEnded } from './legacyTransition';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import {
    GenerationSafeSnapshotCache,
    type SnapshotCacheGeneration,
} from './generationSafeSnapshotCache';
export {
    buildPrivateProjectUpgradeMessage,
    buildRssUpgradeMessage,
    canAccessRssDelivery,
    canUsePrivateProjects,
    getRssUpgradeAction,
    shouldPresentLegacyGraceAccess,
    shouldPromptBillingPreviewCheckout,
    shouldBlockPrivateProjects,
    shouldBlockRssDelivery,
} from './uiEntitlementFeatures';

const DEFAULT_BASE_URL = 'https://paperzilla.ai';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8001';
const ENTITLEMENTS_PATH = '/api/entitlements';
const ENTITLEMENTS_REQUEST_TIMEOUT_MS = 8000;
const DEFAULT_ENTITLEMENTS_CACHE_TTL_SECONDS = 300;
const PROJECT_LIMIT_ERROR_CODE = 'PROJECT_LIMIT_REACHED';
const PROJECT_SUBSCRIPTION_ERROR_CODE = 'PROJECT_SUBSCRIPTION_REQUIRED';
const SUMMARY_LIMIT_ERROR_CODE = 'SUMMARY_LIMIT_REACHED';
const ACTIVE_BILLING_PREVIEW_STATUSES = new Set(['configured', 'running']);

type JsonRecord = Record<string, unknown>;

export type EntitlementSource = 'subscription' | 'legacy_grace' | 'pre_billing_preview' | 'free' | 'worker_fallback';

export interface EntitlementSnapshot {
    org_id?: string | null;
    generated_at?: string | null;
    cache_ttl_seconds?: number | null;
    plan?: JsonRecord | null;
    subscription?: JsonRecord | null;
    billing_preview?: JsonRecord | null;
    legacy_transition?: JsonRecord | null;
    features?: JsonRecord;
    usage?: JsonRecord;
    derived?: JsonRecord;
}

export interface ProjectLimitDetail {
    code?: string | null;
    active_projects?: number | string | null;
    max_projects?: number | string | null;
    entitlement_source?: string | null;
}

export interface SummaryLimitDetail {
    code?: string | null;
    summaries_this_month?: number | string | null;
    max_summaries_per_month?: number | string | null;
    entitlement_source?: string | null;
}

export interface FetchCurrentEntitlementsOptions {
    forceRefresh?: boolean;
    userId?: string | null;
}

const entitlementSnapshotCache = new GenerationSafeSnapshotCache<EntitlementSnapshot>();
const db = supabase as any;

export class EntitlementSnapshotInvalidatedError extends Error {
    constructor() {
        super('Entitlement snapshot was invalidated while loading.');
        this.name = 'EntitlementSnapshotInvalidatedError';
    }
}

export class ProjectLimitReachedError extends Error {
    code = PROJECT_LIMIT_ERROR_CODE;
    snapshot: EntitlementSnapshot | null;

    constructor(message: string, snapshot: EntitlementSnapshot | null = null) {
        super(message);
        this.name = 'ProjectLimitReachedError';
        this.snapshot = snapshot;
        this.code = requiresSubscriptionForProjectCreation(snapshot)
            ? PROJECT_SUBSCRIPTION_ERROR_CODE : PROJECT_LIMIT_ERROR_CODE;
    }
}

export class SummaryLimitReachedError extends Error {
    code = SUMMARY_LIMIT_ERROR_CODE;
    snapshot: EntitlementSnapshot | null;

    constructor(message: string, snapshot: EntitlementSnapshot | null = null) {
        super(message);
        this.name = 'SummaryLimitReachedError';
        this.snapshot = snapshot;
    }
}

function trimTrailingSlash(url: string): string {
    return url.replace(/\/+$/, '');
}

function getConfiguredBaseUrl(): string {
    return trimTrailingSlash(process.env.EXPO_PUBLIC_BASE_URL || DEFAULT_BASE_URL);
}

function isLocalWebDevHost(): boolean {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
}

function isSameOriginOnWeb(url: string): boolean {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    try {
        return new URL(url).origin === window.location.origin;
    } catch {
        return false;
    }
}

function pushCandidate(candidates: string[], endpoint: string): void {
    if (!candidates.includes(endpoint)) {
        candidates.push(endpoint);
    }
}

function getEntitlementEndpointCandidates(): string[] {
    const candidates: string[] = [];
    const configuredEndpoint = `${getConfiguredBaseUrl()}${ENTITLEMENTS_PATH}`;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const sameOriginConfigured = isSameOriginOnWeb(configuredEndpoint);

        if (isLocalWebDevHost()) {
            if (sameOriginConfigured) {
                pushCandidate(candidates, ENTITLEMENTS_PATH);
            }
            pushCandidate(candidates, `${DEFAULT_LOCAL_API_BASE_URL}${ENTITLEMENTS_PATH}`);
            if (!sameOriginConfigured) {
                pushCandidate(candidates, configuredEndpoint);
            }
            return candidates;
        }

        pushCandidate(candidates, ENTITLEMENTS_PATH);
        if (!sameOriginConfigured) {
            pushCandidate(candidates, configuredEndpoint);
        }
        return candidates;
    }

    pushCandidate(candidates, configuredEndpoint);
    return candidates;
}

function asRecord(value: unknown): JsonRecord | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as JsonRecord;
}

function parseIntValue(value: unknown, defaultValue = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.trunc(value);
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }

    if (typeof value === 'string' && value.trim()) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : defaultValue;
    }

    return defaultValue;
}

function parseBooleanValue(value: unknown, defaultValue = false): boolean {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', 't', '1', 'yes', 'y', 'on'].includes(normalized)) {
            return true;
        }
        if (['false', 'f', '0', 'no', 'n', 'off'].includes(normalized)) {
            return false;
        }
    }

    return defaultValue;
}

async function parseErrorResponse(response: Response): Promise<string> {
    // i18n-ignore: technical-token -- transport detail is normalized before product rendering.
    let message = `Request failed (${response.status})`;

    try {
        const body = await response.json();
        if (body?.detail) {
            message = String(body.detail);
        } else if (body?.message) {
            message = String(body.message);
        } else if (body?.error) {
            message = String(body.error);
        }
    } catch {
        try {
            const text = await response.text();
            if (text) {
                message = text;
            }
        } catch {
            // Keep default message.
        }
    }

    if (response.status === 401) {
        // i18n-ignore: technical-token -- transport error code; views render localized copy.
        return 'AUTH_SESSION_EXPIRED';
    }

    return message;
}

function normalizeSnapshot(payload: unknown): EntitlementSnapshot {
    const snapshot = asRecord(payload) || {};

    return {
        ...snapshot,
        features: asRecord(snapshot.features) || {},
        usage: asRecord(snapshot.usage) || {},
        derived: asRecord(snapshot.derived) || {},
        billing_preview: asRecord(snapshot.billing_preview) || {},
        legacy_transition: asRecord(snapshot.legacy_transition) || {},
    };
}

function entitlementCacheScope(accessToken: string, userId?: string | null): string {
    const normalizedUserId = userId?.trim();
    return normalizedUserId ? `user:${normalizedUserId}` : `token:${accessToken}`;
}

function acceptSnapshot(
    scopeKey: string,
    generation: SnapshotCacheGeneration,
    snapshot: EntitlementSnapshot
): EntitlementSnapshot {
    if (!entitlementSnapshotCache.isCurrent(scopeKey, generation)) {
        throw new EntitlementSnapshotInvalidatedError();
    }

    const ttlSeconds = Math.max(
        parseIntValue(snapshot.cache_ttl_seconds, DEFAULT_ENTITLEMENTS_CACHE_TTL_SECONDS),
        0
    );

    if (ttlSeconds > 0) {
        entitlementSnapshotCache.write(
            scopeKey,
            snapshot,
            Date.now() + ttlSeconds * 1000,
            generation
        );
    }

    return snapshot;
}

export function invalidateEntitlementSnapshotCache(userId?: string | null): void {
    const normalizedUserId = userId?.trim();
    entitlementSnapshotCache.invalidate(normalizedUserId ? `user:${normalizedUserId}` : undefined);
}

export function clearEntitlementSnapshotCache(): void {
    invalidateEntitlementSnapshotCache();
}

async function fetchCurrentEntitlementsViaSupabaseRpc(): Promise<EntitlementSnapshot> {
    const { data, error } = await db.rpc('get_current_user_entitlement_snapshot');

    if (error) {
        throw toError(error);
    }

    return normalizeSnapshot(data);
}

export async function fetchCurrentEntitlements(
    accessToken: string,
    options: FetchCurrentEntitlementsOptions = {}
): Promise<EntitlementSnapshot> {
    try {
        return await fetchCurrentEntitlementsOnce(accessToken, options);
    } catch (error) {
        const userId = options.userId?.trim();
        if (!(error instanceof EntitlementSnapshotInvalidatedError) || !userId) {
            throw error;
        }

        // An auth refresh can invalidate a successful response. Retry once with
        // a new generation and current credentials, never across an account change.
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
            throw toError(sessionError);
        }
        if (!session?.access_token || session.user.id !== userId) {
            throw error;
        }
        return fetchCurrentEntitlementsOnce(session.access_token, {
            ...options,
            userId,
            forceRefresh: true,
        });
    }
}

async function fetchCurrentEntitlementsOnce(
    accessToken: string,
    options: FetchCurrentEntitlementsOptions
): Promise<EntitlementSnapshot> {
    const scopeKey = entitlementCacheScope(accessToken, options.userId);
    const requestGeneration = entitlementSnapshotCache.captureGeneration(scopeKey);
    const cachedSnapshot = options.forceRefresh
        ? null
        : entitlementSnapshotCache.read(scopeKey);
    if (cachedSnapshot) {
        return cachedSnapshot;
    }

    if (isLocalWebDevHost()) {
        const snapshot = await fetchCurrentEntitlementsViaSupabaseRpc();
        return acceptSnapshot(scopeKey, requestGeneration, snapshot);
    }

    const endpoints = getEntitlementEndpointCandidates();
    let lastError: Error | null = null;

    for (let index = 0; index < endpoints.length; index += 1) {
        const endpoint = endpoints[index];
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeoutId = controller
            ? setTimeout(() => controller.abort(), ENTITLEMENTS_REQUEST_TIMEOUT_MS)
            : null;

        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                ...(controller ? { signal: controller.signal } : {}),
            });

            if (!response.ok) {
                const message = await parseErrorResponse(response);
                const hasMoreCandidates = index < endpoints.length - 1;
                const shouldRetry = hasMoreCandidates && (response.status === 404 || response.status === 405);

                if (shouldRetry) {
                    lastError = new Error(message);
                    continue;
                }

                throw new Error(message);
            }

            const snapshot = normalizeSnapshot(await response.json());
            return acceptSnapshot(scopeKey, requestGeneration, snapshot);
        } catch (error: any) {
            if (error instanceof EntitlementSnapshotInvalidatedError) {
                throw error;
            }
            if (error?.name === 'AbortError') {
                lastError = new Error('Entitlement request timed out.');
                continue;
            }

            if (error instanceof TypeError) {
                lastError = new Error('Could not reach the entitlement endpoint.');
                continue;
            }

            lastError = error instanceof Error ? error : new Error('Failed to fetch entitlements.');
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }

    try {
        const snapshot = await fetchCurrentEntitlementsViaSupabaseRpc();
        return acceptSnapshot(scopeKey, requestGeneration, snapshot);
    } catch (fallbackError) {
        throw toError(fallbackError || lastError || new Error('Unable to load entitlements right now.'));
    }
}

export function getMaxProjects(snapshot: EntitlementSnapshot | null | undefined): number {
    return parseIntValue(snapshot?.features?.max_projects, 0);
}

export function getActiveProjects(snapshot: EntitlementSnapshot | null | undefined): number {
    return parseIntValue(snapshot?.usage?.active_projects, 0);
}

export function getActiveProjectsRemaining(snapshot: EntitlementSnapshot | null | undefined): number {
    if (snapshot?.derived && 'active_projects_remaining' in snapshot.derived) {
        return parseIntValue(snapshot.derived.active_projects_remaining, 0);
    }

    return Math.max(getMaxProjects(snapshot) - getActiveProjects(snapshot), 0);
}

export function getMaxSummariesPerMonth(snapshot: EntitlementSnapshot | null | undefined): number {
    return parseIntValue(snapshot?.features?.max_summaries_per_month, 0);
}

export function getSummariesThisMonth(snapshot: EntitlementSnapshot | null | undefined): number {
    return parseIntValue(snapshot?.usage?.summaries_this_month, 0);
}

export function getSummaryGenerationsRemaining(snapshot: EntitlementSnapshot | null | undefined): number {
    if (snapshot?.derived && 'summary_generations_remaining' in snapshot.derived) {
        return parseIntValue(snapshot.derived.summary_generations_remaining, 0);
    }

    return Math.max(getMaxSummariesPerMonth(snapshot) - getSummariesThisMonth(snapshot), 0);
}

export function getEntitlementSource(snapshot: EntitlementSnapshot | null | undefined): EntitlementSource {
    const source = typeof snapshot?.derived?.entitlement_source === 'string'
        ? snapshot.derived.entitlement_source
        : '';

    if (
        source === 'subscription'
        || source === 'legacy_grace'
        || source === 'pre_billing_preview'
        || source === 'worker_fallback'
    ) {
        return source;
    }

    return 'free';
}

export function isLegacyEntitlementGrace(snapshot: EntitlementSnapshot | null | undefined): boolean {
    return parseBooleanValue(snapshot?.derived?.legacy_entitlement_grace, false)
        || getEntitlementSource(snapshot) === 'legacy_grace';
}

export function requiresBillingPreviewCheckout(snapshot: EntitlementSnapshot | null | undefined): boolean {
    return parseBooleanValue(snapshot?.billing_preview?.requires_checkout, false);
}

export function getBillingPreviewStatus(snapshot: EntitlementSnapshot | null | undefined): string | null {
    const value = snapshot?.billing_preview?.status;
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function canAccessForwardInbox(snapshot: EntitlementSnapshot | null | undefined): boolean {
    if (!snapshot) {
        return true;
    }

    if (snapshot.derived && 'can_access_forward_inbox' in snapshot.derived) {
        return parseBooleanValue(snapshot.derived.can_access_forward_inbox, true);
    }

    if (parseBooleanValue(snapshot.features?.access_forward_inbox, false)) {
        return true;
    }

    if (isLegacyEntitlementGrace(snapshot)) {
        return true;
    }

    if (getEntitlementSource(snapshot) === 'worker_fallback') {
        return true;
    }

    const previewStatus = getBillingPreviewStatus(snapshot);
    if (previewStatus && ACTIVE_BILLING_PREVIEW_STATUSES.has(previewStatus)) {
        return true;
    }

    return getActiveProjects(snapshot) <= 0;
}

export function shouldBlockForwardInbox(snapshot: EntitlementSnapshot | null | undefined): boolean {
    return Boolean(snapshot) && !canAccessForwardInbox(snapshot);
}

export function canCreateProject(snapshot: EntitlementSnapshot | null | undefined): boolean {
    return canCreateProjectForSnapshot(snapshot);
}

export function shouldBlockProjectCreation(snapshot: EntitlementSnapshot | null | undefined): boolean {
    return shouldBlockProjectCreationForSnapshot(snapshot);
}

export function canGenerateSummary(snapshot: EntitlementSnapshot | null | undefined): boolean {
    if (!snapshot) {
        return true;
    }

    if (snapshot.derived && 'can_generate_summary' in snapshot.derived) {
        return parseBooleanValue(snapshot.derived.can_generate_summary, true);
    }

    const maxSummaries = getMaxSummariesPerMonth(snapshot);
    return maxSummaries > 0 && getSummariesThisMonth(snapshot) < maxSummaries;
}

export function shouldBlockSummaryGeneration(snapshot: EntitlementSnapshot | null | undefined): boolean {
    return Boolean(snapshot)
        && !isLegacyEntitlementGrace(snapshot)
        && !canGenerateSummary(snapshot);
}

export function shouldPromptSummaryTrialActivation(snapshot: EntitlementSnapshot | null | undefined): boolean {
    return Boolean(snapshot)
        && !isLegacyAccessEnded(snapshot)
        && getEntitlementSource(snapshot) === 'free'
        && !isLegacyEntitlementGrace(snapshot)
        && getMaxSummariesPerMonth(snapshot) <= 0;
}

export function buildProjectLimitMessage(snapshot: EntitlementSnapshot | null | undefined): MessageDescriptor {
    return buildProjectLimitMessageForSnapshot(snapshot);
}

export function buildSummaryLimitMessage(snapshot: EntitlementSnapshot | null | undefined): MessageDescriptor {
    const summariesThisMonth = getSummariesThisMonth(snapshot);
    const maxSummaries = getMaxSummariesPerMonth(snapshot);
    const entitlementSource = getEntitlementSource(snapshot);
    const hasFiniteLimit = maxSummaries > 0 && maxSummaries < 1000000;

    if (isLegacyAccessEnded(snapshot)) {
        return getLegacyUpgradeAction(snapshot).hasOffer
            ? msg({ id: 'summary.limit.freeOffer', message: 'Your account is now on the Free plan. View your early-user offer to create new AI summaries.' })
            : msg({ id: 'summary.limit.freePlans', message: 'Your account is now on the Free plan. View plans to create new AI summaries.' });
    }

    if (shouldPromptSummaryTrialActivation(snapshot)) {
        return msg({ id: 'summary.limit.activateTrial', message: 'AI summaries are available during your trial and on paid plans. Activate your 14-day trial to create this summary now. No obligation to buy.' });
    }

    if (hasFiniteLimit) {
        return {
            ...msg({ id: 'summary.limit.monthlyCount', message: "You've used {used} of {maximum} AI summaries for this month. View plans to add more, or wait until next month." }),
            values: { used: summariesThisMonth, maximum: maxSummaries },
        };
    }

    return msg({ id: 'summary.limit.currentPlan', message: 'Your current plan does not allow another AI summary right now. View plans to add summaries.' });
}

export function buildForwardInboxUpgradeMessage(snapshot: EntitlementSnapshot | null | undefined): MessageDescriptor {
    if (isLegacyAccessEnded(snapshot)) {
        return getLegacyUpgradeAction(snapshot).hasOffer
            ? msg({ id: 'inbox.limit.freeOffer', message: 'Your account is now on the Free plan. View your early-user offer to resume processing and matching forwarded alerts.' })
            : msg({ id: 'inbox.limit.freePlans', message: 'Your account is now on the Free plan. View plans to resume processing and matching forwarded alerts.' });
    }

    if (shouldPromptBillingPreviewCheckout(snapshot)) {
        return msg({ id: 'inbox.limit.activateTrial', message: 'Your first topic is ready. Start a 14-day free trial to keep processing forwarded alerts and matching them to your topic. No obligation to buy.' });
    }

    if (isLegacyEntitlementGrace(snapshot)) {
        return msg({ id: 'inbox.limit.legacyGrace', message: 'Forwarded Inbox is included with your legacy access during the billing transition. Refresh and try again.' });
    }

    return msg({ id: 'inbox.limit.paidPlans', message: 'Forwarded Inbox is available on paid plans. You can still forward alerts to Paperzilla; start a 14-day free trial when you want us to process and match them again.' });
}

function parseProjectLimitDetail(value: unknown): ProjectLimitDetail | null {
    if (!value) {
        return null;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        return value as ProjectLimitDetail;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed || ![PROJECT_LIMIT_ERROR_CODE, PROJECT_SUBSCRIPTION_ERROR_CODE].some(code => trimmed.includes(code))) {
        return null;
    }

    try {
        return JSON.parse(trimmed) as ProjectLimitDetail;
    } catch {
        return { code: trimmed.includes(PROJECT_SUBSCRIPTION_ERROR_CODE)
            ? PROJECT_SUBSCRIPTION_ERROR_CODE : PROJECT_LIMIT_ERROR_CODE };
    }
}

function parseSummaryLimitDetail(value: unknown): SummaryLimitDetail | null {
    if (!value) {
        return null;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        return value as SummaryLimitDetail;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed || !trimmed.includes(SUMMARY_LIMIT_ERROR_CODE)) {
        return null;
    }

    try {
        return JSON.parse(trimmed) as SummaryLimitDetail;
    } catch {
        return { code: SUMMARY_LIMIT_ERROR_CODE };
    }
}

function snapshotFromProjectLimitDetail(detail: ProjectLimitDetail | null): EntitlementSnapshot | null {
    if (!detail) {
        return null;
    }

    return {
        features: {
            max_projects: detail.max_projects ?? 0,
        },
        usage: {
            active_projects: detail.active_projects ?? 0,
        },
        derived: {
            can_create_project: false,
            project_creation_requires_subscription: detail.code === PROJECT_SUBSCRIPTION_ERROR_CODE,
            entitlement_source: detail.entitlement_source || 'free',
            legacy_entitlement_grace: false,
            active_projects_remaining: 0,
        },
        billing_preview: {},
    };
}

function snapshotFromSummaryLimitDetail(detail: SummaryLimitDetail | null): EntitlementSnapshot | null {
    if (!detail) {
        return null;
    }

    return {
        features: {
            max_summaries_per_month: detail.max_summaries_per_month ?? 0,
        },
        usage: {
            summaries_this_month: detail.summaries_this_month ?? 0,
        },
        derived: {
            can_generate_summary: false,
            entitlement_source: detail.entitlement_source || 'free',
            legacy_entitlement_grace: false,
            summary_generations_remaining: 0,
        },
        billing_preview: {},
    };
}

function errorText(error: unknown): string {
    if (!error) {
        return '';
    }

    if (typeof error === 'string') {
        return error;
    }

    if (error instanceof Error) {
        return error.message || '';
    }

    if (typeof error === 'object') {
        const record = error as JsonRecord;
        return [
            record.message,
            record.details,
            record.hint,
            record.code,
        ]
            .filter((value) => typeof value === 'string')
            .join(' ');
    }

    return String(error);
}

export function isProjectLimitReachedError(error: unknown): boolean {
    if (error instanceof ProjectLimitReachedError) {
        return true;
    }

    if (!error || typeof error !== 'object') {
        return [PROJECT_LIMIT_ERROR_CODE, PROJECT_SUBSCRIPTION_ERROR_CODE].some(code => errorText(error).includes(code));
    }

    const record = error as JsonRecord;
    if (record.code === PROJECT_LIMIT_ERROR_CODE || record.code === PROJECT_SUBSCRIPTION_ERROR_CODE) {
        return true;
    }

    return [PROJECT_LIMIT_ERROR_CODE, PROJECT_SUBSCRIPTION_ERROR_CODE].some(code => errorText(error).includes(code));
}

export function isSummaryLimitReachedError(error: unknown): boolean {
    if (error instanceof SummaryLimitReachedError) {
        return true;
    }

    if (!error || typeof error !== 'object') {
        return errorText(error).includes(SUMMARY_LIMIT_ERROR_CODE);
    }

    const record = error as JsonRecord;
    if (record.code === SUMMARY_LIMIT_ERROR_CODE) {
        return true;
    }

    return errorText(error).includes(SUMMARY_LIMIT_ERROR_CODE);
}

export function toProjectLimitReachedError(
    error: unknown,
    snapshot: EntitlementSnapshot | null = null
): ProjectLimitReachedError {
    if (error instanceof ProjectLimitReachedError) {
        return error;
    }

    const record = asRecord(error);
    const detail = parseProjectLimitDetail(record?.details)
        || parseProjectLimitDetail(record?.hint)
        || parseProjectLimitDetail(record?.message)
        || parseProjectLimitDetail(errorText(error));
    const resolvedSnapshot = snapshot || snapshotFromProjectLimitDetail(detail);

    return new ProjectLimitReachedError(PROJECT_LIMIT_ERROR_CODE, resolvedSnapshot);
}

export function toSummaryLimitReachedError(
    error: unknown,
    snapshot: EntitlementSnapshot | null = null
): SummaryLimitReachedError {
    if (error instanceof SummaryLimitReachedError) {
        return error;
    }

    const record = asRecord(error);
    const detail = parseSummaryLimitDetail(record?.details)
        || parseSummaryLimitDetail(record?.hint)
        || parseSummaryLimitDetail(record?.message)
        || parseSummaryLimitDetail(errorText(error));
    const resolvedSnapshot = snapshot || snapshotFromSummaryLimitDetail(detail);

    return new SummaryLimitReachedError(SUMMARY_LIMIT_ERROR_CODE, resolvedSnapshot);
}

export async function preflightProjectCreation(accessToken: string): Promise<EntitlementSnapshot | null> {
    const snapshot = await fetchCurrentEntitlements(accessToken, { forceRefresh: true });
    if (!snapshot || parseBooleanValue(snapshot.derived?.entitlement_fallback, false)) {
        throw new Error('PROJECT_ENTITLEMENTS_UNAVAILABLE');
    }

    if (shouldBlockProjectCreation(snapshot)) {
        throw new ProjectLimitReachedError(PROJECT_LIMIT_ERROR_CODE, snapshot);
    }

    return snapshot;
}

export async function preflightSummaryGeneration(accessToken: string): Promise<EntitlementSnapshot | null> {
    let snapshot = await fetchCurrentEntitlements(accessToken);

    if (shouldBlockSummaryGeneration(snapshot)) {
        snapshot = await fetchCurrentEntitlements(accessToken, { forceRefresh: true });
    }

    if (shouldBlockSummaryGeneration(snapshot)) {
        throw new SummaryLimitReachedError(SUMMARY_LIMIT_ERROR_CODE, snapshot);
    }

    return snapshot;
}
